import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, QueuedOfflineError, api } from '@whinerp/client';
import {
  Button,
  ConfirmDialog,
  ConsentChips,
  ErrorState,
  FollowButton,
  LayoutRenderer,
  Panel,
  RecordHeader,
  Skeleton,
  Timeline,
  useToast,
  type FieldConflict,
  type FieldFinding,
  type TimelineEntry,
  type QuickAction,
} from '@whinerp/ui';
import { planLayout, type LayoutMode } from '@whinerp/contracts/layout';
import {
  renderFieldValue,
  type FieldDefinition,
  type FieldValue,
  type RenderedValue,
} from '@whinerp/contracts/fields';
import { formatCount, formatRelativeDate } from '@whinerp/contracts/format';
import { isMessageKey, type MessageKey } from '@whinerp/contracts/i18n';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';
import { RecordHistory } from './record-history.js';
import { FilesPanel } from './files-panel.js';
import { CommentsPanel } from './comments-panel.js';
import { QualificationPanel } from './qualification-panel.js';
import { ViewingsPanel } from './viewings-panel.js';

/**
 * The record page, once, for every object.
 *
 * SC-07, SC-08 and SC-10 are this component with a different object key. The
 * shape is the same in every case - header first, timeline as the main column,
 * details beside it - and three near-identical screens would drift within a
 * fortnight of each other. What differs per object is passed in.
 */

/**
 * The object a record belongs to, as a word.
 *
 * Derived from the key and thrown on rather than defaulted, the same way the
 * nav rail derives its labels: a missing label is a mistake somebody made
 * minutes ago, and a plausible wrong word - "Account" over a company, "Lead"
 * over a contact - is one nobody notices for months.
 */
export function objectLabelKey(objectKey: string): MessageKey {
  const candidate = `object.${objectKey}`;
  if (!isMessageKey(candidate)) {
    throw new Error(
      `Object "${objectKey}" has no label: add '${candidate}' to the message catalogue. ` +
        `A record page that guessed would tell somebody they were looking at the wrong kind of thing.`,
    );
  }
  return candidate;
}

export interface RecordPageProps {
  objectKey: string;
  recordId: string;
  title: string;
  subtitle?: React.ReactNode;
  /** Two or three fields inline-editable in the header. */
  headerFieldKeys: string[];
  consent?: Array<{ channel: string; status: 'opted-in' | 'opted-out' | 'unknown' | 'pending' }>;
  /** Rendered above the timeline: a status bar, a score breakdown, a convert button. */
  banner?: React.ReactNode;
  related?: React.ReactNode;
}

/**
 * One line of text for a header field, from whatever the renderer produced.
 *
 * Every branch returns words. The previous version had one branch for text and
 * `String(value)` for everything else, which is how a resolved lookup became
 * "[object Object]" and an unresolved one became a UUID.
 */
function headerText(rendered: RenderedValue | null): string {
  if (!rendered) return '';
  switch (rendered.kind) {
    case 'text':
      return rendered.text;
    case 'absent':
      return rendered.absent.text;
    case 'lookup':
      return rendered.value.name;
    case 'boolean':
      return rendered.value ? 'Yes' : 'No';
    case 'options':
      return rendered.values.map((option) => option.label).join(', ');
    default:
      return '';
  }
}

export function RecordPage({
  objectKey,
  recordId,
  title,
  subtitle,
  headerFieldKeys,
  consent,
  banner,
  related,
}: RecordPageProps): JSX.Element {
  const t = useT();
  const format = useFormat();
  const errorMessage = useErrorMessage();
  const toast = useToast();
  const queryClient = useQueryClient();
  /*
   * The mode is the person's saved preference, not this page's state.
   *
   * It was local state initialised to 'simple', which meant the control in the
   * user menu wrote a value whose only reader was itself, and the toggle on the
   * record reset on every navigation. "Preference persists per user" is the
   * whole acceptance criterion for F-014 and it was the one thing missing.
   *
   * Read from the session and written back through it: one value, one place,
   * and a switch that failed to save leaves the screen showing what is actually
   * stored rather than what was clicked.
   */
  const { session, setPreference } = useSession();
  const mode: LayoutMode = session.preferences.density;
  const setMode = (next: LayoutMode): void => setPreference({ density: next });
  const [types, setTypes] = useState<string[]>([]);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const navigate = useNavigate();

  const fields = useQuery({
    queryKey: ['record-fields', objectKey, recordId],
    queryFn: () => api.getRecordFields({ path: { objectKey, recordId } }),
  });

  /*
   * Noting the visit, so search can offer it back (F-020).
   *
   * An explicit call rather than a side effect of the read above: a GET that
   * writes cannot be retried, prefetched or cached without changing something.
   * Failures are ignored on purpose - not being able to remember that somebody
   * opened a record is not a reason to show them an error about the record.
   */
  const noted = useRef<string | null>(null);
  useEffect(() => {
    if (noted.current === recordId) return;
    noted.current = recordId;
    void api.noteRecordVisit({ path: { objectKey, recordId } }).catch(() => undefined);
  }, [objectKey, recordId]);

  /*
   * The timeline, one page at a time, accumulated (F-030).
   *
   * `useInfiniteQuery` rather than a cursor in state: "Load older" adds to
   * what is on screen, and a plain query keyed by cursor would REPLACE the
   * page - somebody asking for older activity would watch the newest go away,
   * which is the opposite of what they asked for.
   *
   * Reset when the type filter changes, because a page fetched under one
   * filter is a page of a different list.
   */
  const timeline = useInfiniteQuery({
    queryKey: ['timeline', objectKey, recordId, types.join(',')],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api.getRecordTimeline({
        path: { objectKey, recordId },
        query: {
          ...(types.length > 0 ? { types: types.join(',') } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: (last) => last.nextCursor,
  });

  /*
   * The pages, flattened, with the counts taken from the FIRST page.
   *
   * `total` and `types` describe the whole record and do not change as more
   * pages arrive; taking them from the latest page would be right too, but
   * taking them from the first is right even while the next is in flight.
   */
  const timelineHead = timeline.data?.pages[0] ?? null;
  const timelineEntries = (timeline.data?.pages ?? []).flatMap((page) => page.entries);

  const follow = useQuery({
    queryKey: ['follow', objectKey, recordId],
    queryFn: () => api.getRecordFollowState({ path: { objectKey, recordId } }),
  });

  /*
   * The note composer, and the note it writes (F-030).
   *
   * `logRecordActivity` was a published operation with no caller, and the Note
   * quick action was `() => undefined`. The same gap from both ends: an
   * endpoint nobody could reach, and a button that did nothing when pressed.
   *
   * Opened rather than always drawn: a permanently-open form above the
   * timeline is a box somebody has to look past on every record they read.
   */
  const [noting, setNoting] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [cloneName, setCloneName] = useState('');

  /*
   * How the call went (G-067).
   *
   * Only shown once somebody says this is a call. A disposition picker above
   * every note would be three controls to ignore on the ninety per cent of
   * entries that are not calls.
   */
  const [noteKind, setNoteKind] = useState<'note' | 'call'>('note');
  const [callOutcome, setCallOutcome] = useState<'connected' | 'no-answer' | 'voicemail' | 'wrong-number' | 'busy' | 'callback-requested'>('connected');
  const [callDirection, setCallDirection] = useState<'outbound' | 'inbound'>('outbound');
  const [callMinutes, setCallMinutes] = useState('');
  const [noteSubject, setNoteSubject] = useState('');
  const [noteBody, setNoteBody] = useState('');
  /*
   * Who the last note reached.
   *
   * Shown because a mention is a message somebody else will get, and the
   * writer is the only person who can tell it went to the wrong Rana. A
   * composer that said nothing would leave them to find out from the reply.
   */
  const [mentioned, setMentioned] = useState<string[] | null>(null);

  const clone = useMutation({
    mutationFn: () =>
      api.cloneDeal({
        path: { dealId: recordId },
        body: { name: cloneName.trim() || `${title} (copy)`, accountId: null, closeDate: null },
      }),
    onSuccess: (result) => {
      setCloning(false);
      setCloneName('');
      // Straight to the copy: the next thing anybody does is set its close
      // date, and leaving them on the original is a click they did not ask
      // for.
      navigate(`/deals/${result.dealId}`);
    },
  });

  const logNote = useMutation({
    mutationFn: () =>
      api.logRecordActivity({
        path: { objectKey, recordId },
        body: {
          type: noteKind,
          subject: noteSubject.trim(),
          body: noteBody.trim(),
          call:
            noteKind === 'call'
              ? {
                  direction: callDirection,
                  outcome: callOutcome,
                  // Minutes in, seconds stored: nobody times a call in
                  // seconds, and a box asking for them gets guesses.
                  seconds: callMinutes.trim() === '' ? null : Math.round(Number(callMinutes) * 60),
                }
              : null,
        },
      }),
    onSuccess: async (result) => {
      setNoting(false);
      setNoteSubject('');
      setNoteBody('');
      setNoteKind('note');
      setCallMinutes('');
      setMentioned(result.mentioned.map((entry) => entry.name));
      // The timeline, not the record: a note that saved and did not appear is
      // one somebody writes a second time.
      await queryClient.invalidateQueries({ queryKey: ['timeline', objectKey, recordId] });
    },
  });

  const setFollow = useMutation({
    mutationFn: (following: boolean) =>
      api.setRecordFollowState({ path: { objectKey, recordId }, body: { following } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['follow', objectKey, recordId] }),
  });

  /*
   * What the record breaks, and what the last save was refused for (F-091).
   *
   * Two sources and one display. The stored record's findings come from the
   * server on every visit, so a record made by a form that could not ask for a
   * required field opens saying so. A refused save adds what refused it - the
   * edit is still on screen, not in the database, so only the refusal can say
   * what is wrong with it. Both are drawn on their fields with the rule named.
   */
  const rulesOn = useEntitlement('F-091').state === 'on';
  const validation = useQuery({
    queryKey: ['record-validation', objectKey, recordId],
    queryFn: () => api.getRecordValidation({ path: { objectKey, recordId } }),
    enabled: rulesOn,
  });
  const [refused, setRefused] = useState<FieldFinding[]>([]);
  const [warned, setWarned] = useState<FieldFinding[]>([]);
  const finding = (
    entry: {
      field: string;
      message: string;
      rule?: string;
      messageKey?: string;
      messageValues?: Record<string, string>;
      ruleKey?: string;
    },
    severity: 'warn' | 'block',
  ): FieldFinding => ({
    field: entry.field,
    message: errorMessage(entry),
    rule: entry.ruleKey !== undefined && isMessageKey(entry.ruleKey) ? t(entry.ruleKey) : (entry.rule ?? ''),
    severity,
  });
  const seenFindings = new Set<string>();
  const findings = [
    ...refused,
    ...warned,
    ...(validation.data?.findings ?? []).map((entry) => finding(entry, entry.severity)),
  ].filter((entry) => {
    // The same rule on the same field, said once: a refusal and the stored
    // record's own finding are usually the same sentence.
    const key = `${entry.severity}|${entry.field}|${entry.rule}`;
    if (seenFindings.has(key)) return false;
    seenFindings.add(key);
    return true;
  });

  const save = useMutation({
    mutationFn: (changes: Record<string, { from: FieldValue; to: FieldValue }>) =>
      api.saveRecordFields({ path: { objectKey, recordId }, body: { changes } }),
    onSuccess: (result) => {
      setRefused([]);
      setWarned(result.warnings.map((entry) => finding(entry, entry.severity)));
      void queryClient.invalidateQueries({ queryKey: ['record-validation', objectKey, recordId] });
      void queryClient.invalidateQueries({ queryKey: ['record-fields', objectKey, recordId] });
      if (result.saved.length > 0) {
        toast.show({
          tone: result.conflicts.length > 0 ? 'info' : 'success',
          title: t('state.saved'),
          body: t('record.saved.count', {
            values: { count: formatCount(result.saved.length, format) },
          }),
        });
      }
    },
    onError: (error: unknown) => {
      /*
       * Three outcomes, not two.
       *
       * A write kept on this device is neither a success nor a failure, and
       * calling it either is the specific lie this feature exists to avoid: a
       * success toast tells somebody their colleague can see the note, and an
       * error toast tells them to type it again. So it gets its own tone and
       * its own words, which say where the work is and who cannot see it yet.
       */
      if (error instanceof QueuedOfflineError) {
        toast.show({ tone: 'info', title: t('offline.kept'), body: t('offline.keptBody') });
        return;
      }
      if (error instanceof ApiError && error.code === 'RULE_BLOCKED') {
        setRefused((error.problems ?? []).map((problem) => finding(problem, 'block')));
      }
      const message = error instanceof Error ? errorMessage(error) : t('error.unexpected');
      toast.show({ tone: 'error', title: t('state.error'), body: message });
    },
  });

  /**
   * Saves, or hands the write to the device and says which happened.
   *
   * Returns null when the write was kept rather than sent. The caller clears
   * the fields on null, because the work is durable and a form that stayed
   * dirty would invite a second save into a queue that already holds the
   * first - two writes of one change, the second conflicting with the one in
   * front of it. A genuine failure still rejects, and the fields stay put.
   */
  const saveOrQueue = async (
    body: Record<string, { from: FieldValue; to: FieldValue }>,
  ): Promise<Awaited<ReturnType<typeof api.saveRecordFields>> | null> => {
    try {
      return await save.mutateAsync(body);
    } catch (error) {
      // Reported already, by the mutation's own onError. Swallowed here so the
      // renderer treats it as "done, nothing in conflict" rather than as a
      // failure that should keep the form open.
      if (error instanceof QueuedOfflineError) return null;
      throw error;
    }
  };

  /**
   * Delete, and the ten seconds that make it safe.
   *
   * The specification is blunt about this and it is right: most restores
   * happen within seconds, not days. A person deletes the wrong row, sees it
   * go, and wants it back immediately - and if the only way back is a recycle
   * bin in Setup they will not find it while they still remember what they
   * deleted.
   *
   * So the undo is on the toast, the toast stays for ten seconds rather than
   * the usual six, and restoring is one press. The bin is the fallback for the
   * ones that get away.
   */
  const remove = useMutation({
    mutationFn: () => api.deleteRecord({ path: { objectKey, recordId } }),
    onSuccess: (result) => {
      if (!result.restorable) {
        /*
         * Somebody else deleted it first. Offering undo here would restore a
         * record another person meant to remove, on the strength of a button
         * this person pressed for a different reason.
         */
        toast.show({ tone: 'info', title: t('record.alreadyDeleted') });
        return;
      }
      toast.show({
        tone: 'success',
        title: t('record.deleted', { values: { name: title } }),
        body: t('record.deleted.body'),
        action: { label: t('record.undo'), onClick: () => undo.mutate() },
      });
      navigate(-1);
    },
    onError: (error: unknown) =>
      toast.show({
        tone: 'error',
        title: t('state.error'),
        body: error instanceof Error ? error.message : t('error.unexpected'),
      }),
  });

  const undo = useMutation({
    mutationFn: () => api.restoreRecord({ path: { objectKey, recordId } }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      toast.show({ tone: 'success', title: t('record.restored', { values: { name: title } }) });
    },
    onError: (error: unknown) =>
      // An undo that reported success without restoring would be the worst
      // failure this feature has: the person stops looking, because they were
      // told it worked.
      toast.show({
        tone: 'error',
        title: t('state.error'),
        body: error instanceof Error ? error.message : t('error.unexpected'),
      }),
  });

  const plan = useMemo(() => {
    if (!fields.data) return null;
    return planLayout({
      layout: fields.data.layout,
      fields: fields.data.fields as FieldDefinition[],
      record: fields.data.record as Record<string, FieldValue>,
      mode,
      ctx: format,
    });
  }, [fields.data, mode, format]);

  if (fields.isError) {
    return (
      <ErrorState
        title={t('record.notFound')}
        body={t('record.notFound.body')}
        onRetry={() => void fields.refetch()}
      />
    );
  }

  const entries: TimelineEntry[] = timelineEntries.map((entry) => ({
    id: entry.id,
    type: entry.type,
    typeLabel: activityTypeLabel(t, entry.type),
    typeIcon: ACTIVITY_ICONS[entry.type] ?? 'Circle',
    subject: entry.subject,
    body: entry.body,
    whenLabel: formatRelativeDate(entry.occurredAt, format),
    actorName: entry.actorName,
    actorType: entry.actorType,
    // An agent entry always carries its reasoning. Never null on one.
    reasoningHref: entry.agentRunId ? `/agents/runs/${entry.agentRunId}` : null,
  }));

  return (
    <div data-component="RecordPage" data-object={objectKey} className="flex w-full flex-col gap-5">
      <RecordHeader
        kicker={t(objectLabelKey(objectKey))}
        title={title}
        subtitle={subtitle}
        fields={headerFieldKeys.map((key) => {
          const field = fields.data?.fields.find((f) => f.key === key);
          const value = fields.data?.record[key] ?? null;
          const rendered = field
            ? renderFieldValue(
                field as FieldDefinition,
                value as FieldValue,
                (fields.data?.record ?? {}) as Record<string, FieldValue>,
                format,
              )
            : null;
          return {
            key,
            label: field?.label ?? key,
            /*
             * Whatever the renderer produced, never the raw value.
             *
             * This fell back to `String(value)` for every shape but text - so
             * a lookup rendered its resolved object as "[object Object]", and
             * an unresolved one rendered the identifier itself. A UUID must
             * never reach a person's eyes, and the header was the one place in
             * the product that showed them one.
             */
            value: headerText(rendered),
            /*
             * A lookup is not editable in the header.
             *
             * The header's editor is a text input, and typing a company's name
             * into a field that holds an identifier cannot do anything useful.
             * It was offering exactly that, pre-filled with a UUID.
             */
            editable:
              Boolean(field) &&
              field?.type !== 'formula' &&
              field?.type !== 'rollup' &&
              field?.type !== 'lookup' &&
              field?.type !== 'user',
          };
        })}
        /*
         * Only the two that do something.
         *
         * Call and Email were in this list behind `.filter(key === 'note')`,
         * which removed them - two controls written, neither ever drawn. The
         * real quick-action set belongs here once each one is wired; a button
         * that opens nothing is worse than an absent one.
         */
        actions={
          [
            {
              key: 'note',
              label: t('record.action.note'),
              icon: 'Note',
              onClick: () => setNoting(true),
            },
            /*
             * Cloning, on deals only (G-090). The confirmation is where the
             * name is asked for, because two deals with the same name is how
             * somebody updates the wrong one.
             */
            ...(objectKey === 'deal'
              ? [
                  {
                    key: 'clone',
                    label: t('record.action.clone'),
                    icon: 'Copy' as const,
                    onClick: () => setCloning(true),
                  },
                ]
              : []),
            {
              key: 'delete',
              label: t('record.delete'),
              icon: 'Trash',
              onClick: () => void setConfirmingDelete(true),
            },
          ] satisfies QuickAction[]
        }
        aside={
          <>
            {consent ? (
              <ConsentChips
                consent={consent}
                labels={{
                  optedIn: t('consent.optedIn'),
                  optedOut: t('consent.optedOut'),
                  pending: t('consent.pending'),
                  unknown: t('consent.unknown'),
                  channel: {
                    email: t('consent.channel.email'),
                    sms: t('consent.channel.sms'),
                    whatsapp: t('consent.channel.whatsapp'),
                    call: t('consent.channel.call'),
                    post: t('consent.channel.post'),
                  },
                }}
              />
            ) : null}
            {follow.data ? (
              <FollowButton
                following={follow.data.following}
                followers={formatCount(follow.data.followers, format)}
                labels={{
                  follow: t('record.follow'),
                  following: t('record.following'),
                  followerCount: (count) => t('record.followers', { values: { count } }),
                }}
                onChange={(next) => setFollow.mutate(next)}
              />
            ) : null}
          </>
        }
        labels={{
          edit: t('record.edit'),
          save: t('action.save'),
          cancel: t('action.cancel'),
          more: t('record.more'),
        }}
        onSave={async (changes) => {
          const body: Record<string, { from: FieldValue; to: FieldValue }> = {};
          for (const [key, value] of Object.entries(changes)) {
            body[key] = { from: (fields.data?.record[key] ?? null) as FieldValue, to: value };
          }
          await saveOrQueue(body);
        }}
      />

      {banner}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-record">
        <Panel data-region="timeline">
          {/* Who the last note reached. A mention is a message somebody else
              will get, and the writer is the only person who can tell it went
              to the wrong Rana. */}
          {mentioned === null || mentioned.length === 0 ? null : (
            <p data-note-mentioned className="pb-2 text-meta text-success-text">
              {t('note.mentioned', { values: { names: mentioned.join(', ') } })}
            </p>
          )}
          {noting ? (
            <form
              data-note-composer
              className="mb-3 flex flex-col gap-2 rounded-card bg-sunken p-3"
              onSubmit={(event) => {
                event.preventDefault();
                logNote.mutate();
              }}
            >
              {/* What kind of entry this is, first: it decides whether the
                  call fields are worth showing at all. */}
              <div role="group" aria-label={t('record.note.kind')} className="flex gap-2">
                {(['note', 'call'] as const).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    data-note-kind={kind}
                    aria-pressed={noteKind === kind}
                    onClick={() => setNoteKind(kind)}
                    className={
                      noteKind === kind
                        ? 'h-btn-sm rounded-control bg-bronze-700 px-3 text-dense text-white'
                        : 'h-btn-sm rounded-control border border-control bg-surface px-3 text-dense text-ink'
                    }
                  >
                    {t(kind === 'note' ? 'record.note.kind.note' : 'record.note.kind.call')}
                  </button>
                ))}
              </div>

              {noteKind === 'call' ? (
                <div data-call-fields className="flex flex-wrap gap-2">
                  <label className="flex flex-col gap-1 text-dense text-ink">
                    {t('record.call.outcome')}
                    <select
                      data-call-outcome
                      value={callOutcome}
                      onChange={(event) => setCallOutcome(event.target.value as typeof callOutcome)}
                      className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
                    >
                      {(['connected', 'no-answer', 'voicemail', 'wrong-number', 'busy', 'callback-requested'] as const).map((outcome) => (
                        <option key={outcome} value={outcome}>
                          {t(`record.call.outcome.${outcome}` as 'record.call.outcome.connected')}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 text-dense text-ink">
                    {t('record.call.direction')}
                    <select
                      data-call-direction
                      value={callDirection}
                      onChange={(event) => setCallDirection(event.target.value as typeof callDirection)}
                      className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
                    >
                      <option value="outbound">{t('record.call.outbound')}</option>
                      <option value="inbound">{t('record.call.inbound')}</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 text-dense text-ink">
                    {t('record.call.minutes')}
                    <input
                      data-call-minutes
                      inputMode="decimal"
                      value={callMinutes}
                      onChange={(event) => setCallMinutes(event.target.value)}
                      className="h-input w-20 rounded-control border border-control bg-surface px-2 text-body text-ink"
                    />
                  </label>
                </div>
              ) : null}

              <label className="flex flex-col gap-1 text-dense text-ink">
                {t('record.note.subject')}
                <input
                  data-note-subject
                  autoFocus
                  value={noteSubject}
                  onChange={(event) => setNoteSubject(event.target.value)}
                  className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
                />
              </label>
              <label className="flex flex-col gap-1 text-dense text-ink">
                {t('record.note.body')}
                <textarea
                  data-note-body
                  rows={3}
                  value={noteBody}
                  onChange={(event) => setNoteBody(event.target.value)}
                  className="rounded-control border border-control bg-surface px-2 py-1 text-body text-ink"
                />
              </label>
              <div className="flex items-center gap-2">
                <Button
                  type="submit"
                  intent="primary"
                  size="small"
                  data-note-save
                  busy={logNote.isPending}
                  /*
                   * A subject is the whole entry on a timeline. Without one the
                   * record gains a line saying nothing happened, which is worse
                   * than the note not being there - so the control refuses
                   * rather than writing a blank.
                   */
                  disabled={noteSubject.trim() === ''}
                >
                  {t('record.note.save')}
                </Button>
                <Button
                  intent="ghost"
                  size="small"
                  data-note-cancel
                  onClick={() => {
                    setNoting(false);
                    setNoteSubject('');
                    setNoteBody('');
                  }}
                >
                  {t('record.note.cancel')}
                </Button>
                {logNote.isError ? (
                  <span data-note-refusal className="text-meta text-danger-text">
                    {errorMessage(logNote.error)}
                  </span>
                ) : null}
              </div>
            </form>
          ) : null}

          {timeline.isPending ? (
            <TimelineSkeleton />
          ) : (
            <Timeline
              entries={entries}
              types={(timelineHead?.types ?? []).map((entry) => ({
                type: entry.type,
                label: activityTypeLabel(t, entry.type),
                count: entry.count,
              }))}
              selectedTypes={types}
              onTypesChange={setTypes}
              total={formatCount(timelineHead?.total ?? 0, format)}
              shown={formatCount(entries.length, format)}
              hasMore={timeline.hasNextPage}
              // Was () => undefined: a Load older that took the click and left
              // the record showing exactly what it showed before, which reads
              // as older activity having been lost.
              onLoadMore={() => void timeline.fetchNextPage()}
              labels={{
                all: t('record.timeline.all'),
                byAgent: t('record.timeline.byAgent'),
                bySystem: t('record.timeline.bySystem'),
                whyDidThis: t('record.timeline.why'),
                empty: t('record.timeline.empty'),
                emptyAction: t('record.timeline.emptyAction'),
                expand: t('record.timeline.expand'),
                collapse: t('record.timeline.collapse'),
                loadMore: t('record.timeline.loadMore'),
                showing: (shown, total) =>
                  t('record.timeline.showing', { values: { shown, total } }),
              }}
            />
          )}
        </Panel>

        <div className="flex flex-col gap-5">
          {/* Files, above the details: an attachment is the thing somebody
              came to the record to find more often than a field is. */}
          <FilesPanel objectKey={objectKey} recordId={recordId} />

          {/* Beside the timeline, never in it: an activity is something that
              happened with the customer, a comment is colleagues deciding what
              to do about it (G-077). */}
          {/* Above the discussion on a lead or a deal: how far this has been
              qualified is the question somebody opens the record with
              (G-053). It renders nothing on objects nobody qualifies. */}
          <QualificationPanel objectKey={objectKey} recordId={recordId} />

          {/* Viewings booked for a deal or a lead, and what happened at them (G-088). */}
          <ViewingsPanel objectKey={objectKey} recordId={recordId} />

          <CommentsPanel objectKey={objectKey} recordId={recordId} recordLabel={title} />

          <Panel data-region="details">
            {plan ? (
              <LayoutRenderer
                plan={plan}
                labels={recordLabels(t)}
                findings={findings}
                onModeChange={setMode}
                onSave={async (changes) => {
                  const body: Record<string, { from: FieldValue; to: FieldValue }> = {};
                  for (const [key, value] of Object.entries(changes)) {
                    body[key] = { from: (fields.data?.record[key] ?? null) as FieldValue, to: value };
                  }
                  const result = await saveOrQueue(body);
                  if (!result) return [];
                  return result.conflicts.map((conflict) => {
                    const field = fields.data?.fields.find((f) => f.key === conflict.field);
                    return {
                      field: conflict.field,
                      yours: conflict.yours as FieldValue,
                      theirs: conflict.theirs as FieldValue,
                      theirsRendered: field
                        ? renderFieldValue(
                            field as FieldDefinition,
                            conflict.theirs as FieldValue,
                            {},
                            format,
                          )
                        : { kind: 'text' as const, text: String(conflict.theirs ?? '') },
                      by: conflict.by,
                    } satisfies FieldConflict;
                  });
                }}
              />
            ) : (
              <Skeleton className="h-block-xl w-full" />
            )}
          </Panel>
          {/* Who changed this record, and what they changed (F-018). Below
              the details rather than behind a tab, because the question it
              answers - "who moved this number" - is asked while looking at the
              number. */}
          <RecordHistory objectKey={objectKey} recordId={recordId} />

          {related}
        </div>
      </div>

      {/* The confirmation names the record and says where it goes. "Are you
          sure?" asks somebody to confirm a decision they have already made;
          what they actually need to know is whether it is recoverable. */}
      <ConfirmDialog
        open={confirmingDelete}
        title={t('record.delete')}
        confirmLabel={t('record.delete')}
        confirmIntent="danger"
        busy={remove.isPending}
        labels={{ cancel: t('action.cancel') }}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={() => {
          setConfirmingDelete(false);
          remove.mutate();
        }}
      >
        {t('record.delete.confirm', { values: { name: title } })}
      </ConfirmDialog>

      {/* Cloning asks for the name and says what comes across, because the
          first question anybody has of a clone button is what it copies -
          and the answer they assume is "everything" (G-090). */}
      <ConfirmDialog
        open={cloning}
        title={t('record.action.clone')}
        confirmLabel={t('record.action.clone')}
        busy={clone.isPending}
        labels={{ cancel: t('action.cancel') }}
        onCancel={() => setCloning(false)}
        onConfirm={() => clone.mutate()}
      >
        <div className="flex flex-col gap-2">
          <p className="text-body text-ink">{t('record.clone.explain')}</p>
          <input
            data-clone-name
            aria-label={t('record.clone.name')}
            placeholder={t('record.clone.name')}
            value={cloneName}
            onChange={(event) => setCloneName(event.target.value)}
            className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
          />
        </div>
      </ConfirmDialog>
    </div>
  );
}

const ACTIVITY_ICONS: Record<string, string> = {
  note: 'Note',
  call: 'Phone',
  email: 'Envelope',
  meeting: 'Users',
  whatsapp: 'ChatCircle',
  sms: 'ChatText',
  'next-step': 'CheckCircle',
};

/** An enum is labelled, never shown raw (15_Naming_Conventions). */
function activityTypeLabel(t: ReturnType<typeof useT>, type: string): string {
  const keys = {
    note: 'activity.type.note',
    call: 'activity.type.call',
    email: 'activity.type.email',
    meeting: 'activity.type.meeting',
    whatsapp: 'activity.type.whatsapp',
    sms: 'activity.type.sms',
    'next-step': 'activity.type.next-step',
  } as const;
  const key = keys[type as keyof typeof keys];
  return key ? t(key) : type;
}

function recordLabels(t: ReturnType<typeof useT>) {
  return {
    yes: t('field.yes'),
    no: t('field.no'),
    download: t('field.download'),
    more: (count: number) => t('field.more', { values: { count: String(count) } }),
    choose: t('field.choose'),
    clear: t('field.clear'),
    simpleMode: t('record.mode.simple'),
    advancedMode: t('record.mode.advanced'),
    showAllFields: (count: number) => t('record.showAll', { values: { count: String(count) } }),
    omission: {
      'field-permission': (count: number) =>
        t('record.omitted.fieldPermission', { values: { count: String(count) } }),
      'advanced-only': (count: number) =>
        t('record.omitted.advancedOnly', { values: { count: String(count) } }),
      'over-budget': (count: number) =>
        t('record.omitted.overBudget', { values: { count: String(count) } }),
      'no-such-field': (count: number) =>
        t('record.omitted.noSuchField', { values: { count: String(count) } }),
    },
    edit: t('record.edit'),
    save: t('action.save'),
    cancel: t('action.cancel'),
    conflictTitle: t('record.conflict.title'),
    conflictBody: t('record.conflict.body'),
    keepYours: t('record.conflict.keepYours'),
    keepTheirs: t('record.conflict.keepTheirs'),
    conflictResolved: t('record.conflict.resolved'),
    savedFieldsCount: (count: number) =>
      t('record.saved.count', { values: { count: String(count) } }),
    ruleName: (rule: string) => t('validation.ruleName', { values: { rule } }),
    findingsElsewhere: t('validation.elsewhere'),
  };
}

export function TimelineSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-2" role="status" aria-live="polite" aria-busy>
      {Array.from({ length: 5 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable w-full" />
      ))}
    </div>
  );
}
